<template>
  <v-dialog :value="value"
            persistent
            max-width="800px">
    <div class="modal-container"
         :style="{ background: $vuetify.theme.dark ? Colors.darkThemeDark3 : 'white' }">
      <div class="d-flex w-full justify-end">
        <v-btn icon
               @click="toClose"
               class="-mt-12 -mr-12">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <img loading="lazy"
           src="https://storage.googleapis.com/keybe/files/bluebird/billing/credit-card.svg"
           alt="credit card"
           class="mb-8">
      <strong>{{ $t('addANewCard') }}</strong>
      <!--div with credit card form-->
      <div class="mt-4 px-16 d-flex justify-center align-center flex-column"
           style="width: 24rem">
        <!--Credit Card number-->
        <div class="d-flex align-center"
             style="width: 24rem">
          <img loading="lazy"
               src="https://storage.googleapis.com/keybe/files/bluebird/billing/credit-card-icon.svg"
               alt="credit card"
               class="mr-2">
          <div :class="`input-container ${isCreditCardValid?'success-border':''}`"
               style="width: 20rem"
               class="details">
            <card-number class="stripe-element card-number details"
                         ref="cardNumber"
                         :stripe="stripeKey"
                         :options="componentOptions"
                         @change="number = $event.complete"
            />
          </div>
        </div>
        <!--Date and cvc-->
        <div class="mt-4 d-flex align-center"
             style="width: 20rem">
          <div class="input-container justify-center details"
               style="width: 10rem">
            <card-expiry class="stripe-element card-expiry details"
                         ref="cardExpiry"
                         :stripe="stripeKey"
                         :options="componentOptions"
                         @change="expiry = $event.complete"
            />
          </div>
          <div class="d-flex align-center"
               style="width: 10rem">
            <span class="mx-2">CVC</span>
            <div class="input-container details"
                 style="width: 5rem">
              <!--              <input type="text" v-model="cvc" placeholder="CVC">-->
              <card-cvc class="stripe-element card-cvc details"
                        ref="cardCvc"
                        :stripe="stripeKey"
                        :options="componentOptions"
                        @change="cvc = $event.complete"
              />
            </div>
            <img loading="lazy"
                 src="https://storage.googleapis.com/keybe/files/bluebird/billing/cvc.svg"
                 alt="cvc"
                 class="ml-2 mt-2">
          </div>
        </div>
        <!--Submit Button-->
        <v-btn :loading="loadingAddCreditCard"
               @click="submit"
               large
               elevation="0"
               dark
               color="#0FAEF6"
               rounded
               class="mt-6">{{ $t('addCard') }}
        </v-btn>

      </div>
    </div>
  </v-dialog>
</template>

<script>
import { CardCvc, CardExpiry, CardNumber, createToken } from 'vue-stripe-elements-plus'
import { EventBus } from '@/eventBus'
import BillingService from '@/components/configuration/Services/BillingService'
import BillingValidations from '@/components/configuration/Billing/BillingValidations'
import { mapActions } from 'vuex'
import Colors from '@/utils/Colors'

export default {
  name: 'AddCreditCardDialog',
  props: ['value', 'upgrade'],
  components: { CardNumber, CardExpiry, CardCvc },
  data () {
    return {
      Colors,
      cardNumberElement: {},
      cardExpiryElement: {},
      cardCvcElement: {},
      dialog: false,
      complete: false,
      loadingAddCreditCard: false,
      number: false,
      expiry: false,
      cvc: false,
      stripeKey: process.env.VUE_APP_STRIPE_API_KEY
    }
  },
  methods: {
    ...mapActions('BillingStore', ['setAppConfig']),
    async submit () {
      if (!this.complete) return EventBus.$emit('toast', 'error', this.$t('mustCompleteAllFields'))
      try {
        this.loadingAddCreditCard = true
        const createTokenResponse = await createToken()
        const cardToken = createTokenResponse.token.id
        const res = await BillingService.addCreditCard({ cardToken, transactionProvider: 'stripe' })
        this.loadingAddCreditCard = false

        if (!res) {
          this.$emit('error')
          this.$emit('input', false)
          this.loadingAddCreditCard = false
          return
        }

        if (this.upgrade && !BillingValidations.isProfessional()) {
          const professionalPlan = await BillingValidations.getProfessionalStablePlan()
          const res = await BillingService.updateAppCongif({ plan: professionalPlan?.id })
          if (res) {
            EventBus.$emit('billing:update-balance')
            this.$emit('input', false)
            this.$emit('finishUpgrade')
          }
        }

        EventBus.$emit('billing:update-credit-card', true)
        this.$emit('input', false)
        this.$emit('success')
        this.loadingAddCreditCard = false
      } catch (e) {
        this.$emit('error')
        this.$emit('input', false)
        this.loadingAddCreditCard = false
        console.log(e)
      }
    },
    toClose () {
      this.$emit('input', false)
    },
    update () {
      this.complete = this.number && this.expiry && this.cvc

      // field completed, find field to focus next
      if (this.number) {
        if (!this.expiry) {
          this.$refs.cardExpiry.focus()
        } else if (!this.cvc) {
          this.$refs.cardCvc.focus()
        }
      } else if (this.expiry) {
        if (!this.cvc) {
          this.$refs.cardCvc.focus()
        } else if (!this.number) {
          this.$refs.cardNumber.focus()
        }
      }
      // no focus magic for the CVC field as it gets complete with three
      // numbers, but can also have four
    }
  },
  computed: {
    componentOptions () {
      const styles = {
        base: {
          color: '#4C535C',
          fontSize: '16px',
          fontFamily: 'Monserrat, sans-serif',
          fontSmoothing: 'antialiased',
          fontWeight: '500',
          lineHeight: '24px',
          '::placeholder': { color: 'rgba(76,83,92,0.3)' },
          ':-webkit-autofill': { color: 'rgba(76,83,92,0.3)' }
        },
        complete: {
          color: '#00CECE',
          iconColor: '#00CECE',
          ':-webkit-autofill': { color: '#00CECE' }
        },
        invalid: {
          color: '#F53C56',
          iconColor: '#F53C56',
          ':-webkit-autofill': { color: '#F53C56' }
        }
      }
      return {
        style: styles,
        classes: {
          base: 'form-stripe-element',
          focus: 'form-stripe-element--focus',
          disabled: 'form-stripe-element--disabled',
          complete: 'form-stripe-element--is-success',
          invalid: 'form-stripe-element--is-error',
          empty: 'form-stripe-element'
        }
      }
    },
    isCreditCardValid () {
      // use regex to validate credit card number
      const regexp = /^(?:(4[0-9]{12}(?:[0-9]{3})?)|(5[1-5][0-9]{14})|(6(?:011|5[0-9]{2})[0-9]{12})|(3[47][0-9]{13})|(3(?:0[0-5]|[68][0-9])[0-9]{11})|((?:2131|1800|35[0-9]{3})[0-9]{11}))$/
      return regexp.test(this.creditCardNumber)
    },
    isDateValid () {
      return /^(0[1-9]|1[0-2])\/?([0-9]{2,4})$/.test(this.month + '/' + this.year)
    },
    isCvcValid () {
      return /^[0-9]{3,4}$/.test(this.cvc)
    },
    isFormValid () {
      return this.isCreditCardValid && this.isDateValid && this.isCvcValid
    }
  },
  watch: {
    number () {
      this.update()
    },
    expiry () {
      this.update()
    },
    cvc () {
      this.update()
    }
  },
  created () {
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/'
    script.defer = true
    document.body.appendChild(script)
  }
}
</script>

<style scoped lang="scss">
.modal-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem
}

input {
  outline: none;
}

.input-container {
  background-color: #F0F4FD;
  border-radius: 30px;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
}

.error-border {
  color: #f53c56;
  border: 1px solid #f53c56;
}

.success-border {
  border: 1px solid #00CECE;
}

.divider {
  width: 1px;
  background-color: #CED4E6;
  margin: 0 0.5rem;
  height: 1.4rem;
}

.stripe-element {
  width: 100%;
}
</style>
